import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../styles/SubscriptionContainer.module.scss';
import SubscriptionAppBar from '../components/SubscriptionAppBar';
import Button from '../../../components/Button';
import { supplementsManagement as events } from '../../../lib/analytics/events';
import { useHistory, useLocation } from 'react-router-dom';
import AppPaths from '../../../AppPaths';
import { BillingSubscription } from './SplashPage';
import { DateTime } from 'luxon';
import { useMutation } from '@apollo/client';
import { setSupplementNextShipDateMutation, supplementsSubscriptionsQuery } from '../../../graphql-operations';
import { logInternalError } from '../../../lib/errors';
import { getLoadingOrErrorElement } from '../../../lib/util';
import * as types from '../../../types';
import ErrorMessage, { useError } from '../../../components/ErrorMessage/ErrorMessage';
import Loading from '../../../components/Loading';
import CancelDeterrentConfirmationModal from './components/CancelDeterrentConfirmationModal';
import { CancellationDeterrents } from './util/types';
import classNames from 'classnames';

interface SkipDeterrentState {
  subscription: BillingSubscription;
  showChangeFrequencyDeterrent: boolean;
  currentSubscriptionOption: types.ISupplementSubscriptionProduct[];
  subscriptionOptions: types.ISupplementSubscriptionProduct[];
}

export default function SkipDeterrent() {
  const { t } = useTranslation();
  const pageName = 'Skip Deterrent';
  const history = useHistory();
  const location = useLocation<SkipDeterrentState>();
  const TIMEZONE = 'America/New_York';
  const { error, errorID, setError } = useError();
  const { subscription, showChangeFrequencyDeterrent, currentSubscriptionOption, subscriptionOptions } = location.state;
  events.pageLoad({ subscriptionId: subscription.id }, pageName);

  const previewSkipDate = subscription.supplementShipmentDates!.previewSkippingShipmentDate;

  const [setShipDateMutation, setShipDateMutationState] = useMutation<
    types.gqlTypes.ECOMMERCE_setSupplementNextShipDate,
    types.gqlTypes.ECOMMERCE_setSupplementNextShipDateVariables
  >(setSupplementNextShipDateMutation, {
    refetchQueries: [{ query: supplementsSubscriptionsQuery }],
    onCompleted: (data) => {
      const success = !!data.setSupplementNextShipDate?.success;
      if (success) {
        history.push({
          pathname: AppPaths.Supplements.Root,
          state: { toast: { success: true, change: 'updated' }, refetch: false },
          search: window.location.search,
        });
        events.setShipDate({ subscriptionId: subscription.id });
      }
      setError(
        data.setSupplementNextShipDate?.reasonForFailure
          ? t('failed_to_update_next_ship_date_reason', { reason: data.setSupplementNextShipDate?.reasonForFailure })
          : t('failed_to_update_next_ship_date'),
      );
    },
    onError(err) {
      setError(err.message);
      logInternalError(err);
    },
  });

  const setShipDateLoading = getLoadingOrErrorElement(setShipDateMutationState.loading, null);

  const handleCancel = () => {
    events.deterrentDeclined({ subscriptionId: subscription.id }, CancellationDeterrents.SKIP);
    if (showChangeFrequencyDeterrent) {
      history.push({
        pathname: AppPaths.Supplements.ChangeFrequency(subscription.id),
        state: { subscription, currentSubscriptionOption, subscriptionOptions },
        search: window.location.search,
      });
    } else {
      history.push({
        pathname: AppPaths.Supplements.Discount(subscription.id),
        state: { subscription },
        search: window.location.search,
      });
    }
  };

  const handleSkip = () => {
    const dateWithTimeZone = DateTime.fromISO(previewSkipDate).setZone(TIMEZONE).set({ hour: 6, minute: 0 }).toJSDate();
    setShipDateMutation({
      variables: {
        input: {
          recurlySubscriptionId: subscription.id,
          nextShipDate: dateWithTimeZone,
        },
      },
    });
  };

  return (
    <>
      <div className={classNames(styles.fullWidthWrapper, styles.updatedFullWidthWrapper)}>
        <SubscriptionAppBar
          backButtonAnalyticsEvent={() => events.goBackFromPage({ subscriptionId: subscription.id }, pageName)}
          closeButtonAnalyticsEvent={() => events.closePage({ subscriptionId: subscription.id }, pageName)}
        />
        <div className={styles.subscriptionContainer}>
          <div className={styles.largeHeader}>
            <h1>{t('skip_next_shipment')}</h1>
          </div>

          {setShipDateLoading ? (
            <Loading className={styles.loading} />
          ) : (
            <div className={styles.content}>
              <p>{t('not_ready_for_your_next_shipment_no_problem')}</p>

              <div className={styles.highlight}>
                <div className={styles.header}>{t('new_shipment_date')}</div>
                <div className={styles.title}>{DateTime.fromISO(previewSkipDate).toFormat('cccc, LLL d')}</div>
              </div>
              <p>{t('your_next_supplement_order_will_ship_on_your_new_scheduled_shipment_date_above')}</p>
              {error && <ErrorMessage errors={[error]} errorID={errorID} />}
            </div>
          )}
        </div>
        <div className={classNames(styles.buttonContainer, styles.updatedButtonContainer)}>
          <CancelDeterrentConfirmationModal
            onContinue={handleSkip}
            header={t('confirm_skip_shipment')}
            body={t('your_next_supplement_order_will_ship_on_date', {
              date: DateTime.fromISO(previewSkipDate).toFormat('cccc, LLL d'),
            })}
            trigger={<Button className={styles.button}>{t('skip_next_shipment')}</Button>}
            deterrent={CancellationDeterrents.SKIP}
          />
          <Button className={styles.button} onClick={handleCancel} tertiary>
            {t('continue_with_cancellation')}
          </Button>
        </div>
      </div>
    </>
  );
}
